import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  createOrUpdateAllergy,
  AllergyCreateUpdatePayload,
} from "../api/allergy";
import { toast } from "react-toastify";
import Loader from "./Loader";

interface AllergyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  allergy?: AllergyCreateUpdatePayload | null;
}

const AllergyModal = ({
  isOpen,
  onClose,
  onSuccess,
  allergy,
}: AllergyModalProps) => {
  const [formData, setFormData] = useState<AllergyCreateUpdatePayload>({
    id: "",
    name: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ name?: string }>({});

  useEffect(() => {
    if (allergy) {
      setFormData({ id: allergy.id, name: allergy.name });
    } else {
      setFormData({ id: "", name: "" });
    }
  }, [allergy]);

  const validate = () => {
    const newErrors: { name?: string } = {};
    if (!formData.name.trim()) {
      newErrors.name = "Allergy name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const isUpdating = !!formData.id;
      await createOrUpdateAllergy(formData);
      toast.success(
        isUpdating
          ? "Allergy updated successfully!"
          : "Allergy created successfully!"
      );
      if (!isUpdating) {
        setFormData({ id: "", name: "" }); // Reset form after creation
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to save allergy.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({ id: "", name: "" });
    setErrors({});
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 mx-2"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {loading && <Loader />}
            <h2 className="brand-heading mb-4">
              {formData.id ? "Edit Allergy" : "Create Allergy"}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-600">
                  Allergy Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    className={`mt-1 w-full p-2 border rounded-xl focus:ring-2 focus:ring-blue-500 ${
                      errors.name ? "border-red-500" : ""
                    }`}
                  />
                </div>
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleCancel}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 rounded-lg bg-[#016683] text-white hover:opacity-90 transition"
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AllergyModal;
